import {
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import React from "react";
import {
  chartOptionDefaults,
  curveOptions,
  sliceLabelOptions,
  typeOptionDefaults,
  visualizationOptionFields,
} from "../widgetConstants";

/* -------------------------------------------------------------------------- */
// The form always holds every option (stored values over the type's
// defaults); only the fields of the selected visualization type are shown
// and sent.
export const optionsToForm = (options, visualizationType) => ({
  ...chartOptionDefaults,
  ...typeOptionDefaults[visualizationType],
  ...(options || {}),
});

// On a type change, options with a type-specific default take the new type's
// default; everything else the admin entered is kept.
export const retypeOptions = (form, toType) => {
  const keys = new Set(
    Object.values(typeOptionDefaults).flatMap((defaults) =>
      Object.keys(defaults)
    )
  );
  const typeDefaults = typeOptionDefaults[toType] || {};
  return [...keys].reduce(
    (acc, key) => ({
      ...acc,
      [key]: key in typeDefaults ? typeDefaults[key] : chartOptionDefaults[key],
    }),
    form
  );
};

export const formToOptions = (form, visualizationType) => {
  const fields = visualizationOptionFields[visualizationType];
  if (!fields) return null;
  return fields.reduce((acc, field) => ({ ...acc, [field]: form[field] }), {});
};

const ranges = {
  fill_opacity: [0, 1],
  line_width: [1, 10],
  bar_width: [0.1, 1],
  x_label_rotation: [0, 90],
};

export const validateOptions = (form, visualizationType) => {
  const fields = visualizationOptionFields[visualizationType] || [];
  const errors = {};
  Object.entries(ranges).forEach(([key, [min, max]]) => {
    if (fields.includes(key) && !(form[key] >= min && form[key] <= max))
      errors[key] = `Between ${min} and ${max}`;
  });
  if (fields.includes("color") && !/^#[0-9A-Fa-f]{6}$/.test(form.color))
    errors.color = "Use a #RRGGBB color";
  return errors;
};

const switches = [
  { key: "show_points", label: "Show points" },
  { key: "show_tooltip", label: "Tooltip on hover" },
  { key: "show_grid", label: "Grid lines" },
  { key: "show_legend", label: "Legend" },
  { key: "begin_at_zero", label: "Y axis starts at zero" },
  { key: "thousands_separator", label: "Thousands separator" },
];

/* -------------------------------------------------------------------------- */
export const VisualizationOptionsEditor = ({
  visualizationType,
  value,
  errors = {},
  onChange,
}) => {
  const fields = visualizationOptionFields[visualizationType];
  if (!fields) return null;
  const has = (key) => fields.includes(key);

  const setOption = (key) => (next) => onChange({ ...value, [key]: next });
  const fieldProps = (key) => ({
    value: value[key],
    onChange: (e) => setOption(key)(e.target.value),
    error: !!errors[key],
    helperText: errors[key],
    variant: "outlined",
    size: "small",
    fullWidth: true,
  });
  const numberProps = (key) => ({
    ...fieldProps(key),
    type: "number",
    onChange: (e) =>
      setOption(key)(e.target.value === "" ? "" : Number(e.target.value)),
  });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle1">Display Options</Typography>
        <Typography variant="body2" color="textSecondary">
          Defaults for every dashboard that shows this widget.
        </Typography>
      </Grid>
      {has("x_axis_title") && (
        <Grid item xs={6}>
          <TextField label="X axis title" {...fieldProps("x_axis_title")} />
        </Grid>
      )}
      {has("y_axis_title") && (
        <Grid item xs={6}>
          <TextField label="Y axis title" {...fieldProps("y_axis_title")} />
        </Grid>
      )}
      {has("value_suffix") && (
        <Grid item xs={4}>
          <TextField
            label="Value suffix"
            placeholder="e.g. h, kg"
            {...fieldProps("value_suffix")}
          />
        </Grid>
      )}
      {has("color") && (
        <Grid item xs={4}>
          <TextField
            label="Color"
            {...fieldProps("color")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <input
                    type="color"
                    value={
                      /^#[0-9A-Fa-f]{6}$/.test(value.color)
                        ? value.color
                        : chartOptionDefaults.color
                    }
                    onChange={(e) => setOption("color")(e.target.value)}
                    style={{ width: 24, height: 24, border: 0, padding: 0 }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}
      {has("fill_opacity") && (
        <Grid item xs={4}>
          <TextField
            label="Fill opacity (0-1)"
            inputProps={{ min: 0, max: 1, step: 0.05 }}
            {...numberProps("fill_opacity")}
          />
        </Grid>
      )}
      {has("curve") && (
        <Grid item xs={6}>
          <TextField select label="Line" {...fieldProps("curve")}>
            {curveOptions.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      {has("slice_labels") && (
        <Grid item xs={4}>
          <TextField
            select
            label="Slice labels"
            {...fieldProps("slice_labels")}
          >
            {sliceLabelOptions.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      {has("line_width") && (
        <Grid item xs={6}>
          <TextField
            label="Line width (px)"
            inputProps={{ min: 1, max: 10 }}
            {...numberProps("line_width")}
          />
        </Grid>
      )}
      {has("bar_width") && (
        <Grid item xs={6}>
          <TextField
            label="Bar width (0.1-1 of slot)"
            inputProps={{ min: 0.1, max: 1, step: 0.05 }}
            {...numberProps("bar_width")}
          />
        </Grid>
      )}
      {has("x_label_rotation") && (
        <Grid item xs={6}>
          <TextField
            label="Max X label rotation (°)"
            inputProps={{ min: 0, max: 90 }}
            {...numberProps("x_label_rotation")}
          />
        </Grid>
      )}
      {switches
        .filter(({ key }) => has(key))
        .map(({ key, label }) => (
          <Grid item xs={6} key={key}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  size="small"
                  checked={!!value[key]}
                  onChange={(e) => setOption(key)(e.target.checked)}
                />
              }
              label={label}
            />
          </Grid>
        ))}
    </Grid>
  );
};
